class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

/* ---------------------------------------------------------------------------
 * Hebrew prefix expansion for search queries.
 *
 * Shopify's storefront index matches whole words, and Hebrew glues its article and its
 * prepositions onto the front of one. So "מעבר" and "המעבר" are two unrelated tokens to
 * the index: on this store each matches a different product and neither query finds the
 * other. options[prefix]=last only widens the end of the last word, never the start.
 *
 * The query is therefore rewritten on submit into the OR form Shopify does understand,
 * one parenthesised group per word so a multi-word query keeps its AND meaning:
 *
 *   מעבר       ->  (מעבר OR המעבר OR ומעבר OR שמעבר OR במעבר OR למעבר OR ממעבר OR כמעבר)
 *   מעבר חד    ->  (מעבר OR המעבר OR ומעבר OR שמעבר) (חד OR החד OR וחד OR שחד)
 *
 * Measured against this store: Shopify silently returns zero results for a query of
 * more than nine terms, so the budget is hard and is shared out between the words.
 * Grouping never costs results, it only adds them: "ריישי" went from 21 to 22.
 *
 * sections/main-search.liquid unwraps this back to what the visitor typed, for display
 * and for its own collection matching, through snippets/search-display-terms.liquid.
 * ------------------------------------------------------------------------- */

/* ה ו ש ב ל מ כ, in the order worth spending a limited term budget on */
const HEBREW_PREFIXES = ['ה', 'ו', 'ש', 'ב', 'ל', 'מ', 'כ'];
const SEARCH_TERM_BUDGET = 9;
const HEBREW_LETTER = /[֐-׿]/;

function hebrewWordVariants(word, limit) {
  if (word.length < 2 || !HEBREW_LETTER.test(word)) return [word];

  /*
   * ה at the front of a longer word is the definite article, so the stem is the rest of
   * it and the variants are built from the stem: "המעבר" has to reach "מעבר" and
   * "ומעבר" alike. The other prefix letters are ambiguous, since "מעבר" is a word in
   * its own right and not מ + עבר, so there the typed word is treated as the stem.
   */
  const stem = word[0] === 'ה' && word.length >= 4 ? word.slice(1) : word;

  const variants = [word, stem];
  for (const prefix of HEBREW_PREFIXES) variants.push(prefix + stem);

  /*
   * The typed word may still be carrying one of the ambiguous prefixes, so the shorter
   * form is offered as well. It goes last, where it only costs a slot when the budget
   * has one to spare.
   */
  if (stem === word && HEBREW_PREFIXES.includes(word[0]) && word.length >= 4) {
    variants.push(word.slice(1));
  }

  return [...new Set(variants)].slice(0, limit);
}

function expandHebrewQuery(query) {
  const trimmed = (query || '').trim();
  if (!trimmed) return trimmed;

  // Leave a query that already speaks Shopify's query language alone, and any query
  // with no Hebrew in it, where prefixes are not how words are built
  if (/[()"]/.test(trimmed) || /\b(OR|AND|NOT)\b/.test(trimmed)) return trimmed;
  if (!HEBREW_LETTER.test(trimmed)) return trimmed;

  const words = trimmed.split(/\s+/);
  const perWord = Math.floor(SEARCH_TERM_BUDGET / words.length);
  if (perWord < 2) return trimmed; // nothing left to spend

  const groups = words.map((word) => hebrewWordVariants(word, perWord));

  // Grouping a query that gained no variants would only add noise to the URL
  if (!groups.some((variants) => variants.length > 1)) return trimmed;

  return groups.map((variants) => `(${variants.join(' OR ')})`).join(' ');
}

/*
 * Bound on the document so it runs after the form's own handlers. Predictive search
 * cancels the submit when the visitor picked a suggestion out of the dropdown, and that
 * has to win, hence the defaultPrevented check.
 */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (event.defaultPrevented || !form || form.getAttribute('role') !== 'search') return;

  const input = form.querySelector('input[name="q"]');
  if (!input) return;

  const expanded = expandHebrewQuery(input.value);
  if (expanded === input.value.trim()) return;

  // Navigate rather than rewrite the field, so the visitor never sees the box flick to
  // a query they did not type
  event.preventDefault();

  const params = new URLSearchParams(new FormData(form));
  params.set('q', expanded);
  window.location.assign(`${form.getAttribute('action') || '/search'}?${params}`);
});
